import React, { useEffect, useRef, useState } from 'react';
import UserCard from './UserCard';
import {
  adminGetUsers,
  adminReviewAction,
  adminUpdateGender,
  adminUpdateLocation,
  callableErrorMessage,
  confirmAction,
  deleteAccount,
  fillUserAddresses,
  reviewNewUsers,
  reviewOldUsers
} from '../../../adminApi';

const PAGE_SIZE = 25;

function VerificationPanel(props) {
  const queue = props.queue || 'new';
  const [ids, setIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const loadGeneration = useRef(0);

  const hydrateAddresses = (loaded, generation) => {
    fillUserAddresses(loaded || [], (updater) => {
      if (generation !== loadGeneration.current) {
        return;
      }
      setUsers(updater);
    }, props.onUsersUpdated);
  };

  const load = (nextQueue) => {
    const selectedQueue = nextQueue || queue;
    const generation = loadGeneration.current + 1;
    loadGeneration.current = generation;
    setBusy(true);
    setError('');
    setSelected([]);
    const request = selectedQueue === 'old' ? reviewOldUsers() : reviewNewUsers();
    request
      .then((userIds) => {
        if (generation !== loadGeneration.current) {
          return [];
        }
        const list = userIds || [];
        setIds(list);
        return adminGetUsers(list.slice(0, PAGE_SIZE));
      })
      .then((loaded) => {
        if (generation !== loadGeneration.current) {
          return;
        }
        const nextUsers = loaded || [];
        setUsers(nextUsers);
        setVisible(PAGE_SIZE);
        setBusy(false);
        hydrateAddresses(nextUsers, generation);
      })
      .catch((err) => {
        if (generation !== loadGeneration.current) {
          return;
        }
        setError(callableErrorMessage(err));
        setBusy(false);
      });
  };

  useEffect(() => {
    load(queue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  const toggle = (userId) => {
    setSelected((current) => (
      current.indexOf(userId) === -1
        ? current.concat(userId)
        : current.filter((id) => id !== userId)
    ));
  };

  const toggleAll = () => {
    if (selected.length === users.length) {
      setSelected([]);
      return;
    }
    setSelected(users.map((user) => user.userId));
  };

  const runAction = (label, work) => {
    if (selected.length === 0) {
      setError('Select at least one user.');
      return;
    }
    if (!confirmAction(label)) {
      return;
    }
    setBusy(true);
    setError('');
    work()
      .then(() => {
        if (props.onChanged) {
          props.onChanged();
        }
        load(queue);
      })
      .catch((err) => {
        setError(callableErrorMessage(err));
        setBusy(false);
      });
  };

  const loadMore = () => {
    const next = Math.min(ids.length, visible + PAGE_SIZE);
    const generation = loadGeneration.current;
    setBusy(true);
    adminGetUsers(ids.slice(visible, next))
      .then((loaded) => {
        const nextUsers = loaded || [];
        setUsers((current) => current.concat(nextUsers));
        setVisible(next);
        setBusy(false);
        hydrateAddresses(nextUsers, generation);
      })
      .catch((err) => {
        setError(callableErrorMessage(err));
        setBusy(false);
      });
  };

  return (
    <section>
      <div className="admin-section-head">
        <div>
          <h2>{queue === 'old' ? 'Re-review' : 'New verification'}</h2>
          <p className="admin-muted">
            {ids.length} in queue · {selected.length} selected
          </p>
        </div>
      </div>
      {error ? <p className="admin-error">{error}</p> : null}
      <div className="admin-actions">
        <button type="button" className="admin-btn" onClick={toggleAll} disabled={users.length === 0}>
          {selected.length === users.length && users.length > 0 ? 'Deselect all' : 'Select all'}
        </button>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          disabled={busy}
          onClick={() => runAction(`Verify ${selected.length} user(s)?`, () => adminReviewAction('verify', selected))}
        >
          Verify
        </button>
        {queue === 'new' ? (
          <button
            type="button"
            className="admin-btn"
            disabled={busy}
            onClick={() => runAction(`Request resubmission for ${selected.length} user(s)?`, () => adminReviewAction('resubmit', selected))}
          >
            Request resubmission
          </button>
        ) : null}
        <button
          type="button"
          className="admin-btn admin-btn--danger"
          disabled={busy}
          onClick={() => runAction(`Block ${selected.length} user(s)?`, () => adminReviewAction('block', selected))}
        >
          Block
        </button>
        <button
          type="button"
          className="admin-btn"
          disabled={busy}
          onClick={() => runAction(`Change ${selected.length} user(s) to real location?`, () => adminUpdateLocation(selected))}
        >
          Change to real location
        </button>
        <button
          type="button"
          className="admin-btn"
          disabled={busy}
          onClick={() => runAction(`Change gender for ${selected.length} user(s)?`, () => adminUpdateGender(selected))}
        >
          Change gender
        </button>
        <button
          type="button"
          className="admin-btn admin-btn--danger"
          disabled={busy}
          onClick={() => runAction(`Delete ${selected.length} account(s)?`, () => Promise.all(selected.map((id) => deleteAccount(id))))}
        >
          Delete
        </button>
      </div>
      {users.length === 0 && !busy ? (
        <p className="admin-empty">No users to review.</p>
      ) : (
        <div className="admin-grid">
          {users.map((user) => (
            <UserCard
              key={user.userId}
              user={user}
              selected={selected.indexOf(user.userId) !== -1}
              onSelect={() => toggle(user.userId)}
              onPreview={props.onPreview}
            />
          ))}
        </div>
      )}
      {visible < ids.length ? (
        <div className="admin-actions">
          <button type="button" className="admin-btn" onClick={loadMore} disabled={busy}>
            Load more
          </button>
        </div>
      ) : null}
      {busy ? <p className="admin-muted">Working…</p> : null}
    </section>
  );
}

export default VerificationPanel;
